
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// module components
import BidsListFilters from '@/modules/BidsModule/components/BidsListFilters/index.vue'
import BidsForm from '@/modules/BidsModule/components/BidsForm/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { BidT, BidsGetBidsParamsT } from '@/core/types/bids.types'
import { TabT } from '@/core/types/components.types'

// realisations
import { bids } from '@/core/realisations/bids.realisation'

// constants
import { BidsListFtlColumns } from '@/core/constants/columns/BidsList.columns'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'bids-list-filters': BidsListFilters,
        'bids-form': BidsForm,
        cheap: Cheap,
        TheTable,
        TableScrollWrapper,
    },
    setup() {
        const { havePermission } = useAccount()

        const state = reactive({
            is_add_bid: false,
        })

        const data_state = reactive(new DataState<BidT>({}))

        const filters_state = reactive({
            limit: 50,
            //
            id: null as number | null,
            route: null as string | null,
            carrierId: null as number | null,
            userId: null as number | null,
            dateOfCreation: null as string | null,
            statusId: null as number | null,
        })

        const notifications = useNotifications()

        const tabs: TabT[] = [
            { id: 1, name: 'Список заявок', path: '/bids' },
            { id: 2, name: 'На согласовании', path: '/bids/to-be-agreed' },
        ]

        const loadBids = async (page: number) => {
            data_state.pagination.current_page = page

            try {
                data_state.is_loading = true

                let params = {
                    page,
                    limit: filters_state.limit,
                    id: filters_state.id,
                    route: filters_state.route,
                    carrierId: filters_state.carrierId,
                    userId: filters_state.userId,
                    dateOfCreation: filters_state.dateOfCreation,
                    statusId: filters_state.statusId,
                }

                const response = await bids.getBids(params)
                data_state.list = response.data
                data_state.pagination.total_pages = response.meta.last_page
                data_state.pagination.total_items = response.meta.total

                data_state.is_failed = false
                data_state.is_completed = true
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadBids(data_state.pagination.current_page)
        }

        onBeforeMount(() => loadBids(1))

        const applyFilters = (params: BidsGetBidsParamsT) => {
            filters_state.id = params.id || null
            filters_state.route = params.route || null
            filters_state.carrierId = params.carrierId || null
            filters_state.userId = params.userId || null
            filters_state.dateOfCreation = params.dateOfCreation || null
            filters_state.statusId = params.statusId || null

            loadBids(1)
        }

        const resetFilters = () => {
            filters_state.id = null
            filters_state.route = null
            filters_state.carrierId = null
            filters_state.userId = null
            filters_state.dateOfCreation = null
            filters_state.statusId = null

            loadBids(1)
        }

        const onBidAdded = () => {
            state.is_add_bid = false
            loadBids(data_state.pagination.current_page)
        }

        const deleteBid = async (row: BidT) => {
            const answer = confirm(`Вы действительно хотите удалить заявку '${row.id}'?`)
            if (!answer) return

            try {
                data_state.is_loading = true

                await bids.deleteBid(row.id)
                notifications.notify({
                    title: `Заявка '${row.id}' удалена`,
                    type: 'success',
                })

                loadBids(1)
            } catch (error) {
                notifications.notifyError({ error, title: 'При удалении заявки произошла ошибка' })
            } finally {
                data_state.is_loading = false
            }
        }

        const saveFile = (content: File | Blob, bid_id: number) => {
            const download_url = window.URL.createObjectURL(content)

            const link = document.createElement('a')
            link.href = download_url
            link.setAttribute('download', `Дополнительный договор по заявке №${bid_id}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const saveExcel = (content: File | Blob) => {
            const download_url = window.URL.createObjectURL(content)

            const link = document.createElement('a')
            link.href = download_url
            link.setAttribute('download', 'Список заявок FTL')
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const downloadContract = async (bid_id: number) => {
            try {
                data_state.is_loading = true

                const response = await bids.downloadContract(bid_id)
                saveFile(response, bid_id)
            } catch (error) {
                notifications.notifyError({ error, title: 'При скачивании произошла ошибка' })
            } finally {
                data_state.is_loading = false
            }
        }

        const resendToContract = async (bid_id: number) => {
            try {
                data_state.is_loading = true

                await bids.resendToContractFtl(bid_id)
                notifications.notify({ title: 'Заявка успешно переотправлена!', type: 'success' })
            } catch (error) {
                notifications.notifyError({ error, title: 'При переотправке произошла ошибка' })
            } finally {
                data_state.is_loading = false
            }
        }

        const exportExcel = async () => {
            try {
                data_state.is_loading = true

                const params = {
                    id: filters_state.id,
                    route: filters_state.route,
                    carrierId: filters_state.carrierId,
                    userId: filters_state.userId,
                    dateOfCreation: filters_state.dateOfCreation,
                    statusId: filters_state.statusId,
                }

                const response = await bids.downloadFtlBidsExcel(params)
                saveExcel(response)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                data_state.is_loading = false
            }
        }

        return {
            state,
            filters_state,
            data_state,
            //
            BidsListFtlColumns,
            loadBids,
            applyFilters,
            resetFilters,
            onBidAdded,
            deleteBid,
            downloadContract,
            resendToContract,
            onLimitChange,
            exportExcel,
            havePermission,
            //
            tabs,
        }
    },
})
